import React, { useState } from "react";
import { toast } from "react-toastify";
import * as platformService from "../../services/platformService";
import { DetailGrid, LoadState, PrimaryButton, SecondaryButton, SlidePanel, formatDateTime, statusBadge, statusLabel, useLoad } from "../../components/ui";

const ACTION_LABELS = { SUBMIT: "Submitted", APPROVE: "Approved", REJECT: "Rejected", SEND_BACK: "Sent back", WITHDRAW: "Withdrawn", ESCALATE: "Escalated", REMIND: "Reminder sent", SKIP: "Skipped", AUTO_APPROVE: "Approved automatically", AUTO_REJECT: "Rejected automatically", RESUBMIT: "Resubmitted" };

// One request from the unified inbox: what it is, who has to decide, what has happened -
// and the decision buttons when it is the caller's turn. `item` = { source, request_id }.
export default function ApprovalDrawer({ item, onClose, onChanged }) {
  const { data, loading, error, reload } = useLoad(() => platformService.getApproval(item.source, item.request_id), [item?.source, item?.request_id], { enabled: !!item });
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (fn, okText) => {
    setBusy(true);
    try {
      const res = await fn();
      if (res?.success) {
        toast.success(res.message || okText);
        setComment("");
        onChanged && onChanged();
        onClose();
      } else toast.error(platformService.responseMessage(res));
    } finally {
      setBusy(false);
    }
  };

  const decide = (action) => {
    if (action !== "APPROVE" && !comment.trim()) {
      toast.error(action === "REJECT" ? "Please give a reason for rejecting" : "Please say what needs to change");
      return;
    }
    run(() => platformService.actOnApproval(item.source, item.request_id, action, comment.trim() || undefined), "Done");
  };

  const details = data?.details ? Object.entries(data.details) : [];
  const canWithdraw = !!data && data.status === "PENDING" && data.is_requester;

  const footer =
    data && data.can_act ? (
      <>
        <SecondaryButton onClick={() => decide("SEND_BACK")} disabled={busy}>
          Send back
        </SecondaryButton>
        <SecondaryButton onClick={() => decide("REJECT")} disabled={busy} style={{ color: "#B91C1C", borderColor: "#B91C1C" }}>
          Reject
        </SecondaryButton>
        <PrimaryButton onClick={() => decide("APPROVE")} disabled={busy}>
          Approve
        </PrimaryButton>
      </>
    ) : canWithdraw ? (
      <>
        <SecondaryButton onClick={onClose}>Close</SecondaryButton>
        <SecondaryButton onClick={() => run(() => platformService.withdrawApproval(item.source, item.request_id), "Withdrawn")} disabled={busy}>
          Withdraw request
        </SecondaryButton>
      </>
    ) : (
      <SecondaryButton onClick={onClose}>Close</SecondaryButton>
    );

  return (
    <SlidePanel show={!!item} onHide={onClose} title={data ? data.request_type_label : "Request"} footer={footer} width={520}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {data ? (
          <>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div>
                <div style={{ fontWeight: 600 }}>{data.requester_name || "—"}</div>
                <div style={{ fontSize: 12, color: "#6B7280" }}>{data.source === "HR" ? "HR" : data.request_type_label}</div>
              </div>
              <span style={statusBadge(data.status)}>{statusLabel(data.status)}</span>
            </div>
            {data.summary ? <p style={{ fontSize: 14 }}>{data.summary}</p> : null}
            {details.length ? <DetailGrid items={details.map(([k, v]) => [k, String(v)])} /> : null}

            <h6 className="mt-4 mb-2">Approval steps</h6>
            {(data.approvers || []).length === 0 ? <div style={{ fontSize: 13, color: "#6B7280" }}>No approvers</div> : null}
            {(data.approvers || []).map((a) => (
              <div key={a.id} className="d-flex justify-content-between align-items-center py-2" style={{ borderBottom: "1px solid #F3F4F6", fontSize: 14 }}>
                <span>
                  Level {a.level_no}: {a.approver_name || "Account owner"}
                  {a.escalated ? <span style={{ color: "#92400E" }}> (escalated)</span> : null}
                </span>
                <span style={statusBadge(a.status)}>{statusLabel(a.status)}</span>
              </div>
            ))}

            <h6 className="mt-4 mb-2">Timeline</h6>
            {(data.timeline || []).map((t) => (
              <div key={t.id} style={{ fontSize: 13, padding: "6px 0", borderBottom: "1px solid #F3F4F6" }}>
                <div>
                  <strong>{ACTION_LABELS[t.action] || statusLabel(t.action)}</strong> · {t.actor_name || (t.via === "SYSTEM" ? "System" : "Account owner")}
                </div>
                <div style={{ color: "#6B7280" }}>{formatDateTime(t.created_at)}</div>
                {t.comment ? <div style={{ marginTop: 2 }}>“{t.comment}”</div> : null}
              </div>
            ))}

            {data.can_act ? (
              <div className="mt-4">
                <label htmlFor="apv-comment" style={{ fontSize: 13, fontWeight: 600 }}>
                  Comment
                </label>
                <textarea id="apv-comment" className="form-control" rows={3} maxLength={1000} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Required to reject or send back" />
              </div>
            ) : null}
          </>
        ) : null}
      </LoadState>
    </SlidePanel>
  );
}
